import * as React from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import { useSession } from '../stores/session.js';
import { AppearanceSection } from '../components/appearance.js';
import { PageHead } from '../components/page-head.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import {
  disconnectMcpConnection,
  getTenantSettings,
  listMcpConnections,
  mcpConnectorUrl,
  setAiAssistant,
  type AiAssistantProvider,
} from '../lib/assistant-settings-api.js';
import { errorMessage } from '../lib/properties.js';
import { AiWalletCard } from '../components/ai-wallet-card.js';
import { MailboxSettingsCard } from '../components/mailbox-settings.js';
import { PrivacyRecordCard } from '../components/privacy-record-card.js';
import { GuideSettingsCard } from '../components/guide-toggle.js';
import { OfficeWebsiteCard } from '../components/office-website-card.js';
import { OfficeCustomDomainCard } from '../components/office-custom-domain-card.js';
import { OfficeTrialCard } from '../components/office-trial-card.js';
import { TariffCard } from '../components/tariff-card.js';
import { CommissionGoalCard } from '../components/commission-goal-card.js';
import { IpSecurityCard } from '../components/ip-security-card.js';
/** Krok POB1 bod 2: pobočky kanceláře + rozsah čtení makléře. */
import { OfficesCard } from '../components/offices-card.js';
import { TeamCard } from '../components/team-card.js';
import { KatastrSettingsCard } from '../components/katastr-settings-card.js';
/** Krok GCAL1 bod 1: Google kalendar maklere (ucet sluzby, bez OAuth). */
import { GoogleCalendarCard } from '../components/google-calendar-card.js';
import { NotificationSettingsCard } from '../components/notification-settings-card.js';
import { PasswordSettingsCard } from '../components/password-settings-card.js';
/** Krok NAP3 bod 2 (A27): portret maklere do kontaktu klientskeho reportu. */
import { PortraitSettingsCard } from '../components/portrait-settings-card.js';

/** Krok UXL1 bod 1 — pětice záložek, pořadí je i pořadí v URL. */
const SETTINGS_TABS = [
  { key: 'ucet', labelKey: 'webAdmin.settings.tabAccount' },
  { key: 'kancelar', labelKey: 'webAdmin.settings.tabOffice' },
  { key: 'integrace', labelKey: 'webAdmin.settings.tabIntegrations' },
  { key: 'ai', labelKey: 'webAdmin.settings.tabAi' },
  { key: 'ochrana-udaju', labelKey: 'webAdmin.settings.tabPrivacy' },
] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number]['key'];
const DEFAULT_SETTINGS_TAB: SettingsTab = 'ucet';

function isSettingsTab(value: unknown): value is SettingsTab {
  return typeof value === 'string' && SETTINGS_TABS.some((tab) => tab.key === value);
}

/**
 * `/nastaveni` — od kroku UXN1 ma vlastni polozku v levem menu (skupina
 * Kancelář, `lib/nav.ts`); avatar menu uz nese jen ucet.
 *
 * Krok UXL1 bod 1 — obsah je rozdelen do peti zalozek (Muj ucet, Kancelar,
 * Integrace, AI, Ochrana udaju). Zalozka je v URL (`?tab=`), vzor jako
 * `?klient=` v `klienti.tsx` — jde na ni odkazovat z jinych modulu (napr.
 * Fakturace -> Kancelar, Asistentka -> AI). Obsah jednotlivych karet se
 * NEMENI, jen se preskladal pod zalozky.
 *
 * Krok UXV4p bod 3 — prepinace vzhledu uz NEJSOU jen tady: jejich kod zije
 * v `components/appearance.tsx` a tataz komponenta je i v nabidce profilu,
 * aby byly na jedno klepnuti z kazde obrazovky. Dve kopie prepinace by se
 * rozesly.
 */
export function NastaveniPage(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: string };
  const activeTab: SettingsTab = isSettingsTab(search.tab) ? search.tab : DEFAULT_SETTINGS_TAB;

  function selectTab(tab: SettingsTab): void {
    void navigate({ to: '.', search: tab === DEFAULT_SETTINGS_TAB ? {} : { tab }, resetScroll: false });
  }

  return (
    <div className="p-6" data-testid="nastaveni-page">
      <PageHead title={t('webAdmin.settings.title', { locale: 'cs' })} subtitle={t('webAdmin.settings.perex', { locale: 'cs' })} />

      {/*
        Krok UXF4 bod 5 (nalez N4) — ZALOZKY JSOU ROLOVATELNA LISTA
        `.rh-a2-tabs`, tatáz, jakou uz nese detail zakazky a Schválení. Do
        UXF4 tu stal vlastni pruh s `overflow-hidden`: na 390 px se pate
        „Ochrana údajů" nevesla a nedala se ani odrolovat — byla proste
        oriznuta. Lista ma vodorovny posuv a na mobilu pravé odsazeni, takze
        posledni zalozka nikdy nekonci na hrane.

        Krok UXF5 bod 7 (nalez A6/N4): posuv ale nikdo z prvniho pohledu
        nepozna — na 390 px byly videt tri z peti sekci a nic nenaznacovalo,
        ze pokracuji (E07). `--wrap` listu pod 480 px zalomi do dvou rad,
        takze vsech pet sekci je objevitelnych bez hadani.
      */}
      <div className="rh-a2-tabs rh-a2-tabs--wrap mb-4 min-w-0" role="tablist" data-testid="nastaveni-tabs">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? 'is-on' : ''}
            data-testid={`nastaveni-tab-${tab.key}`}
            onClick={() => selectTab(tab.key)}
          >
            {t(tab.labelKey, { locale: 'cs' })}
          </button>
        ))}
      </div>

      {/* Krok UXL1 bod 1: jednotna sirka formularu pro vsechny zalozky. */}
      <div style={{ maxWidth: 640 }} data-testid={`nastaveni-tab-panel-${activeTab}`}>
        {activeTab === 'ucet' && (
          <>
            <AppearanceSection />
            {/* Krok NAP3 bod 2: portret, ktery tiskne klientsky report. */}
            <PortraitSettingsCard />
            {/* Krok UXP8: zmena hesla (Better Auth change-password), bez e-mailu. */}
            <PasswordSettingsCard />
            {/* Krok PRU1 bod 3: pruvodce „Co si vyzkoušet" skryt / vratit. */}
            <GuideSettingsCard />
            <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-coming-soon-card">
              <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.comingSoonCardTitle', { locale: 'cs' })}</h2>
              <p className="mb-2 text-sm text-gray-600">{t('webAdmin.settings.comingSoonCardPerex', { locale: 'cs' })}</p>
              <Link to="/co-chystame" className="text-sm underline" data-testid="nastaveni-coming-soon-link">
                {t('webAdmin.settings.comingSoonCardLink', { locale: 'cs' })}
              </Link>
            </div>
          </>
        )}

        {activeTab === 'kancelar' && (
          <>
            {/*
              Krok NAP11 (A25): „Tarif" — co kancelar plati, do kdy to plati a co
              je v cene. Je na zalozce „Kancelář" hned nad kartou zkusebniho
              obdobi (TRIAL1), protoze obe mluvi o tomtez: co ma kancelar
              zaplaceno a jak to zmenit. Sesta zalozka by znamenala zmenu
              chraneneho meridla `nastaveni.spec.tsx` (pet zalozek, nalez N4 o
              sazbe na 390 px) - na to tento krok povoleni nema.
            */}
            <TariffCard />
            {/* Krok TRIAL1 bod 5: stav zkusebniho obdobi kancelare + platba. */}
            <OfficeTrialCard />
            {/*
              Krok GRAF2: mesicni cil provize pro kartu Prehledu. Je KARTA,
              ne sesta zalozka — `nastaveni.spec.tsx` je chranene meridlo na
              pet zalozek (tentyz duvod jako „Tarif" a „Pobočky" vyse).
            */}
            <CommissionGoalCard />
            {/*
              Krok POB1 bod 2: „Pobočky" — seznam, přiřazení makléřů a rozsah
              čtení makléře. Je KARTA, ne šestá záložka: `nastaveni.spec.tsx`
              je chráněné měřidlo na pět záložek (nález N4 o sazbě na 390 px),
              stejný důvod jako u „Tarif" (NAP11) a „Upozornění" (NAP6).
            */}
            <OfficesCard />
            {/*
              Krok QAFIX4b bod 2: „Tým" — kdo v kanceláři je, pozvánka, role,
              pobočka, deaktivace. Hned za „Pobočky", protože pobočka v něm je
              povinné zařazení a bez ní nemá `broker_read_scope='office'` co
              zúžit. Taky KARTA, ne šestá záložka (totéž měřidlo).
            */}
            <TeamCard />
            {/* Krok UI4 (SR6): web kanceláře — zapnutí, adresa, náhled. */}
            <OfficeWebsiteCard />
            {/*
              Krok DOM1: vlastní doména téhož webu. Vlastní karta hned za
              „Web kanceláře" — patří k němu, ale je to jiná věc k vyřízení
              (jeden záznam u registrátora) a karta se kreslí až po zapnutí
              webu (bez webu server doménu odmítne, `site_required`).
            */}
            <OfficeCustomDomainCard />
            {/*
              Krok NAP6 (A14): „Upozornění" — výchozí makléř poptávek bez
              zakázky, pracovní doba, souhrn e-mailem. Je na záložce
              „Kancelář", protože to jsou nastavení KANCELÁŘE (jeden řádek
              `crm_notification_settings` na tenanta), ne osobní volba
              makléře. Šestá záložka by znamenala změnu chráněného měřidla
              `nastaveni.spec.tsx` (pět záložek, nález N4 o sazbě na 390 px)
              — na to tento krok povolení nemá.
            */}
            <NotificationSettingsCard />
            {/*
              Zivy pruchod 13. 10. (V7): tato zalozka slibuje „Nastavení
              kanceláře" (podle hlasky na /fakturace), ale formular
              IČO/DIČ/adresy firmy tu NENI — zije na /fakturace. Primy
              odkaz, aby makler nehledal naslepo.
            */}
            <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-office-billing-card">
              <h2 className="mb-1 text-base font-semibold">
                {t('webAdmin.settings.officeBillingTitle', { locale: 'cs' })}
              </h2>
              <p className="mb-2 text-sm text-gray-600">
                {t('webAdmin.settings.officeBillingPerex', { locale: 'cs' })}
              </p>
              <a href="/fakturace?view=settings" className="text-sm underline" data-testid="nastaveni-office-billing-link">
                {t('webAdmin.settings.officeBillingLink', { locale: 'cs' })}
              </a>
            </div>
            {/*
              Krok QAFIX2 (nalez N03 QA-DEN-20261020): prevod kancelare z
              jineho softwaru mel od IMPORT1 backend, ale zadnou cestu
              v adminu. Tohle je ta cesta — karta na zalozce „Kancelář",
              protoze prevod je jednorazove zarizeni kancelare, ne osobni
              volba maklere. Sesta zalozka by znamenala zmenu chraneneho
              meridla `nastaveni.spec.tsx` (pet zalozek), stejne jako
              u „Tarif" (NAP11) a „Upozornění" (NAP6).
            */}
            <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-office-migration-card">
              <h2 className="mb-1 text-base font-semibold">
                {t('webAdmin.officeMigration.settingsCardTitle', { locale: 'cs' })}
              </h2>
              <p className="mb-2 text-sm text-gray-600">
                {t('webAdmin.officeMigration.settingsCardPerex', { locale: 'cs' })}
              </p>
              <Link to="/import" className="text-sm underline" data-testid="nastaveni-office-migration-link">
                {t('webAdmin.officeMigration.settingsCardLink', { locale: 'cs' })}
              </Link>
            </div>
          </>
        )}

        {activeTab === 'integrace' && (
          <>
            {/* Krok AS1 bod 1: schránka makléře (per uživatel). */}
            <MailboxSettingsCard />
            {/* Krok KN1 bod 2: vlastní klíč kanceláře k API katastru (ČÚZK). */}
            <KatastrSettingsCard />
            {/*
              Krok GCAL1 bod 1: Google kalendář makléře. Je na záložce
              „Integrace" vedle schránky a katastru — je to napojení na cizí
              službu, ne nastavení kanceláře. Šestá záložka by znamenala
              změnu chráněného měřidla `nastaveni.spec.tsx` (pět záložek).
            */}
            <GoogleCalendarCard />
            <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-integrations-portals-card">
              <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.integrationsPortalsTitle', { locale: 'cs' })}</h2>
              <p className="mb-2 text-sm text-gray-600">{t('webAdmin.settings.integrationsPortalsPerex', { locale: 'cs' })}</p>
              <Link to="/portaly" className="text-sm underline" data-testid="nastaveni-integrations-portals-link">
                {t('webAdmin.settings.integrationsPortalsLink', { locale: 'cs' })}
              </Link>
            </div>
          </>
        )}

        {activeTab === 'ai' && (
          <>
            <AssistantConnectorsCard />
            <AiWalletCard />
          </>
        )}

        {activeTab === 'ochrana-udaju' && (
          <>
            <IpSecurityCard />
            <PrivacyRecordCard />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Krok AS2 bod 6 — „AI asistent a konektory": volba providera, adresa
 * konektoru ke zkopírování, 3 kroky pro Claude/ChatGPT a seznam připojených
 * klientů s možností odpojit. Vzhled bez novych komponent (UXR3 tokeny:
 * `rh-card`, `rh-a2-link` - stejne jako zbytek stranky).
 */
function AssistantConnectorsCard(): React.JSX.Element {
  const t2 = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');
  const [savedNote, setSavedNote] = React.useState<string | null>(null);
  const [copyNote, setCopyNote] = React.useState<string | null>(null);

  const settings = useQuery({ queryKey: ['nastaveni', 'assistant-provider'], queryFn: getTenantSettings });
  const connections = useQuery({ queryKey: ['nastaveni', 'mcp-connections'], queryFn: listMcpConnections });
  const connectorUrl = mcpConnectorUrl();

  async function onProviderChange(next: AiAssistantProvider): Promise<void> {
    setSavedNote(null);
    try {
      await setAiAssistant(next);
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'assistant-provider'] });
      setSavedNote(t2('webAdmin.settings.assistantProviderSaved'));
    } catch (e) {
      setSavedNote(t2('webAdmin.settings.assistantProviderError', { error: errorMessage(e) }));
    }
  }

  async function onCopy(): Promise<void> {
    if (connectorUrl === undefined) return;
    try {
      await navigator.clipboard.writeText(connectorUrl);
      setCopyNote(t2('webAdmin.settings.assistantCopied'));
    } catch {
      // clipboard API bez povoleni - ticho, uzivatel si adresu opsat muze rucne
    }
  }

  async function onDisconnect(familyId: string): Promise<void> {
    if (!window.confirm(t2('webAdmin.settings.assistantDisconnectConfirm'))) return;
    try {
      await disconnectMcpConnection(familyId);
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'mcp-connections'] });
    } catch (e) {
      window.alert(t2('webAdmin.settings.assistantDisconnectError', { error: errorMessage(e) }));
    }
  }

  return (
    <div className="rh-card" style={{ maxWidth: 640, padding: 16 }} data-testid="nastaveni-assistant-card">
      <h2 className="mb-1 text-base font-semibold">{t2('webAdmin.settings.assistantTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t2('webAdmin.settings.assistantPerex')}</p>

      <label className="mb-1 block text-sm font-medium" htmlFor="ai-assistant-provider">
        {t2('webAdmin.settings.assistantProviderLabel')}
      </label>
      <select
        id="ai-assistant-provider"
        data-testid="assistant-provider-select"
        className="mb-1 rounded border px-2 py-1 text-sm"
        value={settings.data?.aiAssistant ?? 'off'}
        disabled={!isOwnerOrAdmin || settings.isLoading}
        onChange={(e) => void onProviderChange(e.target.value as AiAssistantProvider)}
      >
        <option value="off">{t2('webAdmin.settings.assistantProviderOff')}</option>
        <option value="claude">{t2('webAdmin.settings.assistantProviderClaude')}</option>
        <option value="gpt">{t2('webAdmin.settings.assistantProviderGpt')}</option>
      </select>
      {savedNote !== null ? <p className="mb-2 text-xs text-gray-600">{savedNote}</p> : null}

      <div className="mb-3 mt-3">
        <span className="mb-1 block text-sm font-medium">{t2('webAdmin.settings.assistantConnectorUrlLabel')}</span>
        {connectorUrl === undefined ? (
          <p className="text-sm text-gray-600" data-testid="assistant-connector-url-missing">
            {t2('webAdmin.settings.assistantConnectorUrlMissing')}
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <code className="rounded bg-gray-100 px-2 py-1 text-xs" data-testid="assistant-connector-url">
              {connectorUrl}
            </code>
            <button type="button" className="rh-a2-link text-xs" onClick={() => void onCopy()}>
              {t2('webAdmin.settings.assistantCopyButton')}
            </button>
            {copyNote !== null ? <span className="text-xs text-gray-600">{copyNote}</span> : null}
          </div>
        )}
      </div>

      <details className="mb-3 text-sm">
        <summary className="cursor-pointer font-medium">{t2('webAdmin.settings.assistantHowToClaudeTitle')}</summary>
        <p>{t2('webAdmin.settings.assistantHowToClaudeStep1')}</p>
        <p>{t2('webAdmin.settings.assistantHowToClaudeStep2')}</p>
        <p>{t2('webAdmin.settings.assistantHowToClaudeStep3')}</p>
      </details>
      <details className="mb-3 text-sm">
        <summary className="cursor-pointer font-medium">{t2('webAdmin.settings.assistantHowToChatGptTitle')}</summary>
        <p>{t2('webAdmin.settings.assistantHowToChatGptStep1')}</p>
        <p>{t2('webAdmin.settings.assistantHowToChatGptStep2')}</p>
        <p>{t2('webAdmin.settings.assistantHowToChatGptStep3')}</p>
      </details>

      <h3 className="mb-1 text-sm font-semibold">{t2('webAdmin.settings.assistantConnectionsTitle')}</h3>
      {connections.error != null ? (
        <p className="text-sm text-gray-600">{t2('webAdmin.settings.assistantConnectionsLoadError')}</p>
      ) : (connections.data ?? []).length === 0 ? (
        <p className="text-sm text-gray-600" data-testid="assistant-connections-empty">
          {t2('webAdmin.settings.assistantConnectionsEmpty')}
        </p>
      ) : (
        <ul data-testid="assistant-connections-list">
          {(connections.data ?? []).map((c) => (
            <li key={c.familyId} className="mb-1 flex items-center justify-between text-sm">
              <span>{c.clientName}</span>
              {isOwnerOrAdmin || c.userId === user?.id ? (
                <button type="button" className="rh-a2-link text-xs" onClick={() => void onDisconnect(c.familyId)}>
                  {t2('webAdmin.settings.assistantDisconnectButton')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
